import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, ButtonBase, createStyles, makeStyles, Typography } from '@material-ui/core';
import { BaseRouteParams } from 'pages/routes';
import { ApplicationState } from 'store/store';
import { PayTableInfo } from '../header/PayTableInfo';
import { ButtonV2 } from '../ui/PayButton';
import { PayBaseModal } from './PayBaseModal';
import { Image } from 'lib/Image';

const useStyles = makeStyles(() =>
    createStyles({
        link: {
            textDecoration: 'none'
        },
        typography: {
            fontWeight: 700,
            fontSize: '15px'
        }
    })
);

interface PayActionsModalProps {
    open: boolean;
    handleChange: () => void;
    table?: string;
    checkId?: string;
    onClose: () => void;
    orderNotFound?: boolean;
    onBillSplit: () => void;
    onAddMoreItems: () => void;
}

export const PayActionModal: React.FC<PayActionsModalProps> = ({
    open,
    handleChange,
    table,
    checkId,
    onClose,
    orderNotFound,
    onBillSplit,
    onAddMoreItems
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { merchantId } = useParams<BaseRouteParams>();
    const [showLogo, setShowLogo] = React.useState<boolean>(true);
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const handleBillSplit = React.useCallback(() => {
        onClose();
        onBillSplit();
    }, [onBillSplit, onClose]);
    const handleLogoFallbackError = React.useCallback(() => {
        setShowLogo(false);
    }, []);
    return (
        <PayBaseModal open={open} onClose={onClose}>
            <Box px={1} py={1.5}>
                {!!showLogo && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        paddingBottom={1}
                        paddingTop={5}
                    >
                        <Image
                            src={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/pat_logo`}
                            onFallbackError={handleLogoFallbackError}
                            height="140px"
                        />
                    </Box>
                )}
                <Box display="flex" justifyContent="center" marginBottom={4}>
                    <ButtonBase onClick={handleChange}>
                        <PayTableInfo isLoading={false} table={table} checkId={checkId} />
                    </ButtonBase>
                </Box>
                {orderNotFound && (
                    <Typography className={classes.typography} align="center">
                        {settings?.orderToTable?.multiPartEnabled
                            ? t('PAT_QUICKPAY_ORDER_NOT_FOUND')
                            : t('PAT_QUICKPAY_ORDER_NOT_FOUND_MPO')}
                    </Typography>
                )}
                <Box>
                    {!!settings?.orderToTable?.multiPartEnabled && (
                        <Box paddingBottom={0.25}>
                            <ButtonV2 onClick={onAddMoreItems} secondary data-cy="add-more">
                                {t('BILL_SUMMARY_ADD_ITEMS_BUTTON')}
                            </ButtonV2>
                        </Box>
                    )}
                    {!!settings?.payAtTable?.noTableCheckFoundUrl && (
                        <Box paddingBottom={0.25}>
                            <a
                                href={settings?.payAtTable?.noTableCheckFoundUrl}
                                rel="noreferrer"
                                target="_blank"
                                className={classes.link}
                            >
                                <ButtonV2 secondary>{t('DIALOG_VIEW_MENU')}</ButtonV2>
                            </a>
                        </Box>
                    )}
                    {!!settings?.billSplittingByPercentageEnabled && !orderNotFound && (
                        <Box paddingBottom={0.25}>
                            <ButtonV2 onClick={handleBillSplit} secondary>
                                {t('VIEW_BILL_SPLIT_BTN')}
                            </ButtonV2>
                        </Box>
                    )}
                    <ButtonV2 disabled={orderNotFound} onClick={onClose}>
                        {t('PAT_QUICKPAY_PAY_THE_BILL')}
                    </ButtonV2>
                </Box>
            </Box>
        </PayBaseModal>
    );
};
